import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import EnrollButton from "../EnrollLink/EnrollLink";
import ReserveLink from "../ReserveLink/ReserveLink";
import "./Shadeule.css";
import RegisterButton from "../RegisterProtect/RegisterButton";

export default function EcaScheduleList() {
  const location = useLocation();
  const selectedRef = useRef(null);

  const [schedules, setSchedules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);

  const params = new URLSearchParams(location.search);
  const selectedCourse = params.get("course");
  const selectedLevel = params.get("level");

  const user = JSON.parse(localStorage.getItem("user"));
  const today = new Date();
  const conversionRate = 89;

  /* ================= FETCH SCHEDULE ================= */
  useEffect(() => {
    fetch("https://agepcoatings.com/eacschedule")
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setSchedules(data.schedules || []);
      })
      .catch(console.error);
  }, []);

  /* ================= FETCH COURSES ================= */
  useEffect(() => {
    fetch("https://agepcoatings.com/getcourses")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setCourses(data);
      })
      .catch(console.error);
  }, []);

  /* ================= FETCH PROFILE ================= */
  useEffect(() => {
    if (!user?.email) {
      setProfileChecked(true);
      return;
    }

    fetch(
      `https://agepcoatings.com/getuserbyemail/${encodeURIComponent(
        user.email
      )}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setProfileData(data.data);
        setProfileChecked(true);
      })
      .catch(() => setProfileChecked(true));
  }, [user?.email]);

  /* ================= HELPERS ================= */
  const normalizeCourse = (str = "") =>
    String(str)
      .toLowerCase()
      .replace(/\(.*?\)/g, "")
      .replace(/program|certification/g, "")
      .replace(/[^a-z]/g, "");

  const getLevelNumber = (val) => {
    const m = String(val).match(/\d+/);
    return m ? Number(m[0]) : null;
  };

  const isSelectedCourse = (courseName) =>
    selectedCourse &&
    normalizeCourse(courseName) === normalizeCourse(selectedCourse);

  const isSelectedLevel = (levelName) =>
    selectedLevel &&
    getLevelNumber(levelName) === getLevelNumber(selectedLevel);

  /* ================= ENROLL DATA ================= */
  const getEnrollData = (sch) => {
    if (!profileData || !courses.length) return null;

    const isMember = profileData.isMember?.toLowerCase() === "yes";
    const country = profileData.country?.toLowerCase();

    const schCourseKey = normalizeCourse(sch.courseName);
    const schLevelNum = getLevelNumber(sch.levelName);

    for (const course of courses) {
      if (normalizeCourse(course.courseName) !== schCourseKey) continue;

      for (const level of course.levelsData || []) {
        if (getLevelNumber(level.levelName) !== schLevelNum) continue;

        const baseAmount = isMember ? level.member_fee : level.nonmember_fee;

        if (!baseAmount) return null;

        return {
          courseId: course.courseId,
          levelId: level.levelId,
          courseTitle: course.courseName,
          levelbadge: level.levelName,
          amount:
            country === "india" ? baseAmount * conversionRate : baseAmount,
        };
      }
    }
    return null;
  };

  /* ================= PRICE DISPLAY ================= */
  const getDisplayPrice = (sch) => {
    if (!courses.length) return null;

    const schCourseKey = normalizeCourse(sch.courseName);
    const schLevelNum = getLevelNumber(sch.levelName);

    for (const course of courses) {
      if (normalizeCourse(course.courseName) !== schCourseKey) continue;

      for (const level of course.levelsData || []) {
        if (getLevelNumber(level.levelName) !== schLevelNum) continue;

        // Guest → show both prices (USD)
        if (!user || !profileData) {
          return [
            { label: "Member", amount: level.member_fee, currency: "USD" },
            {
              label: "Non-Member",
              amount: level.nonmember_fee,
              currency: "USD",
            },
          ];
        }

        // Logged in → single price
        const isMember = profileData.isMember?.toLowerCase() === "yes";
        const country = profileData.country?.toLowerCase();

        let amount = isMember ? level.member_fee : level.nonmember_fee;

        let currency = country === "india" ? "INR" : "USD";
        if (currency === "INR") amount *= conversionRate;

        return {
          label: isMember ? "Member" : "Non-Member",
          amount,
          currency,
        };
      }
    }
    return null;
  };

  /* ================= GROUP BY COURSE ================= */
  const grouped = schedules.reduce((acc, item) => {
    if (!item.courseName) return acc;
    if (!acc[item.courseName]) acc[item.courseName] = [];
    acc[item.courseName].push(item);
    return acc;
  }, {});

  const isFuture = (s) =>
    s.startDate &&
    !isNaN(new Date(s.startDate)) &&
    new Date(s.startDate) > today;

  const buildLevelData = (schedules = []) => {
    const levelMap = {};

    schedules.forEach((sch) => {
      const level = sch.levelName;

      if (!levelMap[level]) {
        levelMap[level] = {
          levelName: level,
          future: [],
        };
      }

      // ✅ collect ONLY future schedules
      if (isFuture(sch)) {
        levelMap[level].future.push(sch);
      }
    });

    // ✅ REMOVE levels that have future schedules from "coming soon"
    Object.keys(levelMap).forEach((level) => {
      if (levelMap[level].future.length > 0) {
        levelMap[level].showComingSoon = false;
      } else {
        levelMap[level].showComingSoon = true;
      }
    });

    return levelMap;
  };

  /* ================= SORT COURSES ================= */
  const sortedCourses = Object.keys(grouped).sort((a, b) => {
    const futureDatesA = grouped[a]
      .filter((s) => s.startDate && new Date(s.startDate) > today)
      .map((s) => new Date(s.startDate));

    const futureDatesB = grouped[b]
      .filter((s) => s.startDate && new Date(s.startDate) > today)
      .map((s) => new Date(s.startDate));

    if (!futureDatesA.length && !futureDatesB.length) return 0;
    if (!futureDatesA.length) return 1;
    if (!futureDatesB.length) return -1;

    const minA = new Date(Math.min(...futureDatesA));
    const minB = new Date(Math.min(...futureDatesB));

    return minA - minB; // 🔥 earliest course first
  });

  /* ================= AUTO SCROLL ================= */
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [schedules]);

  return (
    <div className="container schedule-wrapper py-4">
      {/* <h3 className="fw-bold mt-4 text-center text-muted">
        Site Under Maintenance. We’ll Be Live Soon.
      </h3> */}

      <h3 className="fw-bold mt-4">Course Schedule</h3>

      {!user?.uid && (
        <div className="schedule-register">
          <RegisterButton className="custom-course-req-com-btn border-0" />
        </div>
      )}
      {/* {!sortedCourses.length && (
        <p className="text-center text-muted mt-4">
          No schedules available at the moment.
        </p>
      )} */}

      {sortedCourses.map((courseName) => (
        <div key={courseName} className="course-section mb-4">
          <div className="course-header shadow-lg">
            <span>{courseName}</span>
          </div>

          <div className="course-content">
            <div className="table-head-wrapper">
              <div className="table-column-layout table-head-row">
                <span>Level</span>
                <span>Date</span>
                <span>Location</span>
                <span>Price</span>
                <span>Action</span>
              </div>
            </div>

            <div className="table-scroll-wrapper">
              <table className="course-table">
                <tbody>
                  {Object.values(buildLevelData(grouped[courseName]))
                    .sort((a, b) => {
                      // ✅ LEVEL FIRST
                      return (
                        (getLevelNumber(a.levelName) || 0) -
                        (getLevelNumber(b.levelName) || 0)
                      );
                    })
                    .map(({ levelName, future, showComingSoon }) => {
                      // 🔹 CASE 1: NO FUTURE → SHOW "COMING SOON"
                      // 🔹 SHOW "COMING SOON" ONLY IF NO FUTURE EXISTS
                      if (showComingSoon) {
                        return (
                          <tr
                            key={levelName}
                            className="table-column-layout table-body-row"
                          >
                            <td className="level-cell" data-label="Level">
                              <div className="level-stack">
                                <span className="level-number">
                                  {getLevelNumber(levelName)}
                                </span>
                                <span className="level-text">
                                  {levelName.replace(/\d+/g, "").trim()}
                                </span>
                              </div>
                            </td>
                            <td data-label="Date">Coming soon</td>
                            <td data-label="Location">-</td>
                            <td data-label="Price">-</td>
                            <td data-label="Action"></td>
                          </tr>
                        );
                      }

                      // 🔹 CASE 2: FUTURE EXISTS → SHOW ONLY FUTURE
                      // 🔹 FUTURE EXISTS → SHOW ONLY FUTURE
                      return future
                        .sort(
                          (a, b) =>
                            new Date(a.startDate) - new Date(b.startDate)
                        )
                        .map((sch, index) => {
                          const enrollData = getEnrollData(sch);
                          const price = getDisplayPrice(sch);

                          const isSelected =
                            isSelectedCourse(sch.courseName) &&
                            isSelectedLevel(sch.levelName);

                          return (
                            <tr
                              ref={isSelected ? selectedRef : null}
                              className={`table-column-layout table-body-row ${
                                isSelected ? "selected-schedule-row" : ""
                              }`}
                            >
                              <td className="level-cell" data-label="Level">
                                <div className="level-stack">
                                  <span className="level-number">
                                    {getLevelNumber(sch.levelName)}
                                  </span>
                                  <span className="level-text">
                                    {sch.levelName.replace(/\d+/g, "").trim()}
                                  </span>
                                </div>
                              </td>

                              <td data-label="Date">{sch.dateRange}</td>
                              <td data-label="Location">{sch.location}</td>

                              {/* <td data-label="Price">
                                {!price ? (
                                  "-"
                                ) : (
                                  <div className="price-stack">
                                    {price.label}:{" "}
                                    {price.currency === "INR" ? "₹" : "$"}
                                    {price.amount.toLocaleString()}
                                  </div>
                                )}
                              </td> */}
                              <td data-label="Price">
                                {!price ? (
                                  "-"
                                ) : Array.isArray(price) ? (
                                  <div className="price-stack">
                                    {price.map((p, i) => (
                                      <div key={i}>
                                        {p.label}: ${p.amount.toLocaleString()}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="price-stack">
                                    {price.label}:{" "}
                                    {price.currency === "INR" ? "₹" : "$"}
                                    {price.amount.toLocaleString()}
                                  </div>
                                )}
                              </td>

                              {/* <td data-label="Action">
                                {!user?.uid ||
                                !profileChecked ||
                                !enrollData ? null : (
                                  <div className="schedule-action">
                                    <EnrollButton {...enrollData} />
                                    <ReserveLink {...enrollData} />
                                  </div>
                                )}
                              </td> */}
                              <td data-label="Action">
                                {user?.uid && profileChecked && enrollData ? (
                                  <div className="schedule-action">
                                    <EnrollButton {...enrollData} />
                                    <ReserveLink {...enrollData} />
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          );
                        });
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
